import { motion } from "framer-motion";
import { CalendarCheck, Clock, DatabaseBackup, Gauge, ImagePlus, Link2, Phone, PhoneMissed, ShieldCheck } from "lucide-react";
import { EASE } from "@/lib/lenis";

const stagger = (i) => ({
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.7, ease: EASE, delay: 0.15 + i * 0.12 },
});

export const WebsiteVisual = () => (
  <div className="grid h-full grid-cols-[104px_1fr] gap-3 sm:grid-cols-[150px_1fr] sm:gap-4">
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2 sm:p-3">
      <p className="px-2 pb-2 font-mono text-xs uppercase tracking-wider text-[#A1A1AA]">Pages</p>
      {["Home", "Services", "Service area", "About", "Contact"].map((page, i) => (
        <motion.div
          key={page}
          {...stagger(i)}
          className={`flex items-center justify-between rounded-md px-2 py-1.5 text-xs sm:text-sm ${i === 0 ? "bg-white/[0.07] text-white" : "text-[#A1A1AA]"}`}
        >
          <span className="truncate">{page}</span>
          {i === 0 && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />}
        </motion.div>
      ))}
    </div>
    <div className="relative overflow-hidden rounded-lg border border-white/[0.06] bg-[#121722] p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-mono text-xs text-[#A1A1AA]">yourbusiness.com</span>
        <span className="flex items-center gap-1.5 rounded-full bg-emerald-400/10 px-2 py-0.5 text-xs text-emerald-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Live
        </span>
      </div>
      <div className="mt-6 space-y-2">
        <div className="h-3 w-4/5 rounded bg-white/80" />
        <div className="h-3 w-3/5 rounded bg-white/80" />
        <div className="mt-4 h-2 w-2/3 rounded bg-white/20" />
        <div className="h-2 w-1/2 rounded bg-white/20" />
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <span className="flex items-center gap-1 rounded-full bg-[#2F6BFF] px-3 py-1.5 text-xs font-medium">
          <Phone className="h-3 w-3" aria-hidden="true" /> Call now
        </span>
        <span className="rounded-full border border-white/15 px-3 py-1.5 text-xs">Get a quote</span>
      </div>
      <div className="mt-6 grid grid-cols-3 gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-14 rounded-md border border-white/[0.06] bg-white/[0.03]" />
        ))}
      </div>
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1, ease: EASE, delay: 0.4 }}
        className="absolute -bottom-10 right-3 hidden h-48 w-[100px] rounded-[18px] border border-white/15 bg-[#0A0D14] p-2 shadow-2xl sm:block"
      >
        <div className="h-full rounded-[12px] bg-[#151B27] p-2">
          <div className="h-1.5 w-3/4 rounded bg-white/70" />
          <div className="mt-1.5 h-1.5 w-1/2 rounded bg-white/70" />
          <div className="mt-3 h-1 w-2/3 rounded bg-white/20" />
          <div className="mt-4 h-5 rounded-full bg-[#2F6BFF]" />
          <div className="mt-2 h-10 rounded bg-white/[0.05]" />
        </div>
      </motion.div>
    </div>
  </div>
);

const CARE_ROWS = [
  [ShieldCheck, "Security updates", "Done"],
  [DatabaseBackup, "Site backup", "Done"],
  [Clock, "Holiday hours updated", "Done"],
  [ImagePlus, "New project photos added", "Done"],
  [Gauge, "Speed check", "Scheduled"],
];

export const CareVisual = () => (
  <div className="h-full overflow-hidden rounded-lg border border-white/[0.06] bg-[#11141B]">
    <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-3">
      <p className="text-sm font-medium">Care log</p>
      <span className="font-mono text-xs text-[#A1A1AA]">This month</span>
    </div>
    <ul className="divide-y divide-white/[0.06]">
      {CARE_ROWS.map(([Icon, label, state], i) => (
        <motion.li key={label} {...stagger(i)} className="flex items-center gap-3 px-4 py-3.5">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-white/[0.08] bg-white/[0.03]">
            <Icon className="h-4 w-4 text-[#2DD4D4]" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-white/90">{label}</span>
          <span
            className={`rounded-full px-2 py-0.5 text-xs ${state === "Done" ? "bg-emerald-400/10 text-emerald-300" : "bg-[#2F6BFF]/15 text-[#9BB8FF]"}`}
          >
            {state}
          </span>
        </motion.li>
      ))}
    </ul>
  </div>
);

const Bubble = ({ out, i, children }) => (
  <motion.div {...stagger(i)} className={`flex ${out ? "justify-end" : "justify-start"}`}>
    <p
      className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-snug ${out ? "rounded-br-md bg-[#2F6BFF] text-white" : "rounded-bl-md bg-white/[0.07] text-white/90"}`}
    >
      {children}
    </p>
  </motion.div>
);

export const AutomationVisual = () => (
  <div className="mx-auto w-full max-w-sm rounded-[28px] border border-white/10 bg-[#0D1017] p-2.5">
    <div className="space-y-3 rounded-[20px] bg-[#10141C] p-4">
      <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
        <p className="text-sm font-medium">Kestrel Heating & Plumbing</p>
        <span className="font-mono text-xs text-[#A1A1AA]">Text</span>
      </div>
      <motion.p {...stagger(0)} className="flex items-center justify-center gap-2 text-xs text-[#FCA5A5]">
        <PhoneMissed className="h-3.5 w-3.5" aria-hidden="true" /> Missed call · 2:14 PM
      </motion.p>
      <Bubble out i={1}>
        Sorry we missed your call! This is Kestrel Heating & Plumbing. How can we help?
      </Bubble>
      <Bubble i={2}>Our water heater is leaking. Can someone take a look?</Bubble>
      <Bubble out i={3}>
        We can help. Pick a time that works for you:
        <span className="mt-2 flex items-center gap-1.5 rounded-lg bg-white/15 px-2.5 py-1.5 text-xs">
          <Link2 className="h-3 w-3" aria-hidden="true" /> Book a visit
        </span>
      </Bubble>
      <motion.p {...stagger(4)} className="flex items-center justify-center gap-2 pt-1 text-xs text-[#2DD4D4]">
        <CalendarCheck className="h-3.5 w-3.5" aria-hidden="true" /> Booking request received
      </motion.p>
    </div>
  </div>
);
